import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Video, Calendar, Clock,
  ExternalLink, Play, Check, X, Globe, Save,
  Mail, Send, Languages, BarChart3, Eye, Monitor,
  Smartphone, Tablet, TrendingUp, Users, Bell
} from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

// Admin Live Classes Management
export const AdminLiveClasses = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', course_id: '',
    platform: 'zoom', meeting_url: '', 
    scheduled_at: '', duration_minutes: 60
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [classesRes, coursesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/live-classes`, { headers }),
        axios.get(`${API_URL}/courses`)
      ]);
      setClasses(classesRes.data || []);
      // Handle paginated response from courses API
      const coursesData = coursesRes.data?.courses || coursesRes.data || [];
      setCourses(Array.isArray(coursesData) ? coursesData : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        course_id: formData?.course_id === 'none' ? null : formData?.course_id
      };
      
      if (editingClass) {
        await axios.put(`${API_URL}/admin/live-classes/${editingClass?.id}`, submitData, { headers });
        toast.success(t('admin_toast_live_updated', 'Live class updated'));
      } else {
        await axios.post(`${API_URL}/admin/live-classes`, submitData, { headers });
        toast.success(t('admin_toast_live_created', 'Live class created and notifications sent'));
      }
      setDialogOpen(false);
      setEditingClass(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_live_save_fail', 'Failed to save live class'));
    }
  };

  const handleEdit = (liveClass) => {
    if (!liveClass) return;
    setEditingClass(liveClass);
    setFormData({
      title: liveClass?.title || '',
      description: liveClass?.description || '',
      course_id: liveClass?.course_id || 'none',
      platform: liveClass?.platform || 'zoom',
      meeting_url: liveClass?.meeting_url || '',
      scheduled_at: liveClass?.scheduled_at || '',
      duration_minutes: liveClass?.duration_minutes || 60
    });
    setDialogOpen(true);
  };

  const handleDelete = async (classId) => {
    if (!classId || !window.confirm('Delete this live class?')) return;
    try {
      await axios.delete(`${API_URL}/admin/live-classes/${classId}`, { headers });
      toast.success(t('admin_toast_live_deleted', 'Live class deleted'));
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_live_delete_fail', 'Failed to delete live class'));
    }
  };

  const updateStatus = async (classId, status) => {
    if (!classId) return;
    try {
      await axios.put(`${API_URL}/admin/live-classes/${classId}/status?status=${status}`, {}, { headers });
      toast.success(tFormat('admin_toast_live_status_fmt', { status }, 'Status updated to {status}'));
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_live_status_fail', 'Failed to update status'));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', course_id: 'none',
      platform: 'zoom', meeting_url: '', 
      scheduled_at: '', duration_minutes: 60
    });
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-red-500';
      case 'scheduled': return 'bg-green-500';
      case 'completed': return 'secondary';
      case 'cancelled': return 'destructive';
      default: return 'secondary';
    }
  };

  return (
    <div data-testid="admin-live-classes">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">Live Classes</h1>
          <p className="text-muted-foreground">{classes.length} total classes</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingClass(null); resetForm(); }} data-testid="create-live-class-btn">
              <Plus className="w-4 h-4 mr-2" />
              Schedule Live Class
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingClass ? 'Edit Live Class' : 'Schedule Live Class'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value})} 
                  placeholder="Introduction to Cabinet Design"
                  required 
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})} 
                  placeholder="Join us for an interactive session..."
                  required 
                />
              </div>
              <div>
                <Label>Related Course (Optional)</Label>
                <Select value={formData.course_id || 'none'} onValueChange={(v) => setFormData({...formData, course_id: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific course</SelectItem>
                    {courses.map((course) => (
                      <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Platform</Label>
                  <Select value={formData.platform} onValueChange={(v) => setFormData({...formData, platform: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zoom">Zoom</SelectItem>
                      <SelectItem value="youtube">YouTube Live</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Duration (minutes)</Label>
                  <Input 
                    type="number" 
                    value={formData.duration_minutes} 
                    onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})} 
                  />
                </div>
              </div>
              <div>
                <Label>Meeting URL</Label>
                <Input 
                  value={formData.meeting_url} 
                  onChange={(e) => setFormData({...formData, meeting_url: e.target.value})} 
                  placeholder="https://zoom.us/j/... or https://youtube.com/live/..."
                  required 
                />
              </div>
              <div>
                <Label>Scheduled Date & Time</Label>
                <Input 
                  type="datetime-local" 
                  value={formData.scheduled_at?.slice(0, 16)} 
                  onChange={(e) => setFormData({...formData, scheduled_at: new Date(e.target.value).toISOString()})} 
                  required 
                />
              </div>
              <DialogFooter>
                <Button type="submit">{editingClass ? 'Update' : 'Schedule & Notify'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Scheduled</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {classes.map((liveClass) => (
                <TableRow key={liveClass.id}>
                  <TableCell className="font-medium">{liveClass.title}</TableCell>
                  <TableCell className="capitalize">{liveClass.platform}</TableCell>
                  <TableCell>{formatDate(liveClass.scheduled_at)}</TableCell>
                  <TableCell>{liveClass.duration_minutes} min</TableCell>
                  <TableCell>
                    <Select 
                      value={liveClass.status} 
                      onValueChange={(v) => updateStatus(liveClass.id, v)}
                    >
                      <SelectTrigger className="w-32">
                        <Badge className={getStatusColor(liveClass.status)}>{liveClass.status}</Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => window.open(liveClass.meeting_url, '_blank')}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(liveClass)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(liveClass.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {classes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No live classes scheduled
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Translation Editor
export const AdminTranslations = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState('en');
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedKeys, setEditedKeys] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/translations`, { headers });
      const langData = Array.isArray(res.data) ? res.data : [];
      setLanguages(langData);
      
      // Set translations for selected language
      const lang = langData.find(l => l.code === selectedLang);
      if (lang) {
        setTranslations(lang.translations || {});
      }
    } catch (err) {
      console.error(err);
      setLanguages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    const lang = languages.find(l => l.code === selectedLang);
    if (lang) {
      setTranslations(lang.translations);
      setEditedKeys({});
    }
  }, [selectedLang, languages]);

  const handleChange = (key, value) => {
    setTranslations(prev => ({ ...prev, [key]: value }));
    setEditedKeys(prev => ({ ...prev, [key]: true }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/translations/${selectedLang}`, {
        language: selectedLang,
        translations: translations || {}
      }, { headers });
      toast.success(tFormat('admin_toast_translations_saved', { lang: selectedLang }, 'Translations for {lang} saved'));
      setEditedKeys({});
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_translations_fail', 'Failed to save translations'));
    } finally {
      setSaving(false);
    }
  };

  const translationKeys = Object.keys(translations || {});
  const editedCount = Object.keys(editedKeys || {}).length;

  return (
    <div data-testid="admin-translations">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">Translation Editor</h1>
          <p className="text-muted-foreground">Manage translations for all languages</p>
        </div>
        <Button onClick={handleSave} disabled={saving || editedCount === 0} data-testid="save-translations-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : `Save Changes ${editedCount > 0 ? `(${editedCount})` : ''}`}
        </Button>
      </div>

      {/* Language Selection */}
      <div className="flex flex-wrap gap-3 mb-8">
        {languages.map((lang) => (
          <Button
            key={lang.code}
            variant={selectedLang === lang.code ? 'default' : 'outline'}
            onClick={() => setSelectedLang(lang.code)}
            className="relative"
            data-testid={`lang-${lang.code}`}
          >
            <Globe className="w-4 h-4 mr-2" />
            {lang.name}
            <Badge variant="secondary" className="ml-2 text-xs">
              {Math.round(lang.progress)}%
            </Badge>
          </Button>
        ))}
      </div>

      {/* Translation Progress */}
      {languages.find(l => l.code === selectedLang) && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Translation Progress</span>
              <span className="text-sm text-muted-foreground">
                {languages.find(l => l.code === selectedLang)?.translated_keys} / {languages.find(l => l.code === selectedLang)?.total_keys} keys
              </span>
            </div>
            <Progress value={languages.find(l => l.code === selectedLang)?.progress || 0} className="h-2" />
          </CardContent>
        </Card>
      )}

      {/* Translation Editor */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/3">Key</TableHead>
                  <TableHead className="w-1/3">English (Reference)</TableHead>
                  <TableHead className="w-1/3">
                    {languages.find(l => l.code === selectedLang)?.name || selectedLang}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {translationKeys.map((key) => {
                  const englishLang = languages.find(l => l.code === 'en');
                  const englishValue = englishLang?.translations?.[key] || '';
                  
                  return (
                    <TableRow key={key} className={editedKeys[key] ? 'bg-amber-500/10' : ''}>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {key}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {englishValue}
                      </TableCell>
                      <TableCell>
                        {key.includes('subtitle') || key.includes('desc') || key.includes('text') ? (
                          <Textarea
                            value={translations[key] || ''}
                            onChange={(e) => handleChange(key, e.target.value)}
                            className="min-h-[80px]"
                            placeholder={englishValue}
                            data-testid={`trans-${key}`}
                          />
                        ) : (
                          <Input
                            value={translations[key] || ''}
                            onChange={(e) => handleChange(key, e.target.value)}
                            placeholder={englishValue}
                            data-testid={`trans-${key}`}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Email Notifications
export const AdminEmailNotifications = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  // Separate loading flags — sharing one `sending` boolean between these two
  // unrelated actions made clicking "Send Test Email" visually disable and
  // relabel "Send to All Users" to "Sending...", looking like it had also
  // been triggered.
  const [sendingNotification, setSendingNotification] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState({ recipient_email: '', subject: '', html_content: '' });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axios.get(`${API_URL}/courses`);
        // Handle both array and paginated response
        const coursesData = res.data?.courses || res.data || [];
        setCourses(Array.isArray(coursesData) ? coursesData : []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCourses();
  }, []);

  const sendCourseNotification = async () => {
    if (!selectedCourse) {
      toast.error(t('admin_email_select_course', 'Please select a course'));
      return;
    }
    setSendingNotification(true);
    try {
      await axios.post(`${API_URL}/admin/email/notify-new-course/${selectedCourse}`, {}, { headers });
      toast.success(t('admin_email_notify_sent', 'Notification emails are being sent to all users'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_email_notify_fail', 'Failed to send notifications. Please configure SMTP in Integrations settings.'));
    } finally {
      setSendingNotification(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail?.recipient_email || !testEmail?.subject) {
      toast.error(t('admin_email_fill_fields', 'Please fill in all fields'));
      return;
    }
    setSendingTest(true);
    try {
      await axios.post(`${API_URL}/admin/email/test`, testEmail, { headers });
      toast.success(t('admin_email_test_sent', 'Test email sent'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_email_test_fail', 'Failed to send test email'));
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div data-testid="admin-email">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-medium">Email Notifications</h1>
        <p className="text-muted-foreground">Send email notifications to users</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New Course Notification */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              New Course Notification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Send an email to all users about a new course. This will notify everyone about the course availability.
            </p>
            <div>
              <Label>Select Course</Label>
              <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a course" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map((course) => (
                    <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={sendCourseNotification} disabled={sendingNotification || !selectedCourse}>
              <Send className="w-4 h-4 mr-2" />
              {sendingNotification ? 'Sending...' : 'Send to All Users'}
            </Button>
          </CardContent>
        </Card>

        {/* Test Email */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send Test Email
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Send a test email to verify email configuration is working.
            </p>
            <div>
              <Label>Recipient Email</Label>
              <Input 
                type="email"
                value={testEmail.recipient_email}
                onChange={(e) => setTestEmail({...testEmail, recipient_email: e.target.value})}
                placeholder="test@example.com"
              />
            </div>
            <div>
              <Label>Subject</Label>
              <Input 
                value={testEmail.subject}
                onChange={(e) => setTestEmail({...testEmail, subject: e.target.value})}
                placeholder="Test Email from THACHARI"
              />
            </div>
            <div>
              <Label>HTML Content</Label>
              <Textarea 
                value={testEmail.html_content}
                onChange={(e) => setTestEmail({...testEmail, html_content: e.target.value})}
                placeholder="<h1>Hello!</h1><p>This is a test email.</p>"
                className="font-mono text-sm"
              />
            </div>
            <Button onClick={sendTestEmail} disabled={sendingTest} variant="outline">
              <Send className="w-4 h-4 mr-2" />
              {sendingTest ? 'Sending...' : 'Send Test Email'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Configuration Info */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-2">Email Configuration</h3>
          <p className="text-sm text-muted-foreground">
            Email notifications are sent via the SMTP server configured under Integrations → Email.
            Without a working SMTP configuration, emails will not be sent.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
